import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../Styles/PaymentRecords.css";
import Loader from "../../Components/Loader";

function PaymentRecords() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState([]);
  const [filteredReceipts, setFilteredReceipts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "desc" });
  const [filterOptions, setFilterOptions] = useState({
    financialYear: "",
    paymentMode: "",
    degree: ""
  });

  const [availableOptions, setAvailableOptions] = useState({
    financialYears: [],
    paymentModes: [],
    degrees: []
  });
  
  const itemsPerPage = 10;

  useEffect(() => {
    fetchAllReceipts();
  }, []);

  useEffect(() => {
    // Apply filters when any filter changes
    applyFilters();
  }, [searchTerm, dateRange, filterOptions, receipts, sortConfig]);

  const fetchAllReceipts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get("https://api.uniums.com/api/bill/all-receipts", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        const receiptData = response.data.receipts || [];
        setReceipts(receiptData);
        
        // Extract unique options for filters
        const years = [...new Set(receiptData.map(r => r.financialYear))];
        const modes = [...new Set(receiptData.map(r => r.paymentMode))];
        const degrees = [...new Set(receiptData.map(r => r.degree))];
        
        setAvailableOptions({
          financialYears: years,
          paymentModes: modes,
          degrees: degrees
        });
        
        // Initial sort and filter
        applyFilters();
      } else {
        toast.info("No payment records found.");
      }
    } catch (error) {
      console.error("Error fetching receipts:", error);
      toast.error("Failed to fetch payment records.");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...receipts];
    
    // Apply search term filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(receipt => 
        receipt.studentName.toLowerCase().includes(term) ||
        receipt.receiptNo.toLowerCase().includes(term) ||
        receipt.userCode.toLowerCase().includes(term)
      );
    }
    
    // Apply date range filter
    if (dateRange.from) {
      const fromDate = new Date(dateRange.from);
      filtered = filtered.filter(receipt => 
        new Date(receipt.createdAt) >= fromDate
      );
    }
    
    if (dateRange.to) {
      const toDate = new Date(dateRange.to);
      toDate.setHours(23, 59, 59, 999); // End of the day
      filtered = filtered.filter(receipt => 
        new Date(receipt.createdAt) <= toDate
      );
    }
    
    // Apply dropdown filters
    if (filterOptions.financialYear) {
      filtered = filtered.filter(receipt => 
        receipt.financialYear === filterOptions.financialYear
      );
    }
    
    if (filterOptions.paymentMode) {
      filtered = filtered.filter(receipt => 
        receipt.paymentMode === filterOptions.paymentMode
      );
    }
    
    if (filterOptions.degree) {
      filtered = filtered.filter(receipt => 
        receipt.degree === filterOptions.degree
      );
    }
    
    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    setFilteredReceipts(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const resetFilters = () => {
    setSearchTerm("");
    setDateRange({ from: "", to: "" });
    setFilterOptions({
      financialYear: "",
      paymentMode: "",
      degree: ""
    });
    setSortConfig({ key: "createdAt", direction: "desc" });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const viewReceipt = (pdfUrl) => {
    window.open(pdfUrl, "_blank");
  };

  const viewDetails = (receipt) => {
    setSelectedReceipt(receipt);
    setShowDetailsModal(true);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterOptions(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredReceipts.length / itemsPerPage);
  const currentItems = filteredReceipts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Navigation for pagination
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="payment-records-container">
      <h1 className="page-title">Payment Records</h1>
      <div className="filters-container">
          <div className="date-filters">
            <div className="date-filter">
              <label>From:</label>
              <input
                type="date"
                name="from"
                value={dateRange.from}
                onChange={handleDateChange}
              />
            </div>
            <div className="date-filter">
              <label>To:</label>
              <input
                type="date"
                name="to"
                value={dateRange.to}
                onChange={handleDateChange}
              />
            </div>
          </div>
          
          <div className="dropdown-filters">
            <select
              name="financialYear"
              value={filterOptions.financialYear}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Financial Years</option>
              {availableOptions.financialYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            
            <select
              name="paymentMode"
              value={filterOptions.paymentMode}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Payment Modes</option>
              {availableOptions.paymentModes.map(mode => (
                <option key={mode} value={mode}>{mode}</option>
              ))}
            </select>
            
            <select
              name="degree"
              value={filterOptions.degree}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Degrees/Classes</option>
              {availableOptions.degrees.map(degree => (
                <option key={degree} value={degree}>{degree}</option>
              ))}
            </select>
            
            <button className="reset-btn" onClick={resetFilters}>
              Reset Filters
            </button>
          </div>


          
        </div>
      {/* Search and Filter Section */}
      <div className="controls-container">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by student name, receipt no, or user code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        
      </div>
      
      {/* Summary Stats */}
      <div className="summary-stats">
        <div className="stat-box">
          <span className="stat-label">Total Records</span>
          <span className="stat-value">{filteredReceipts.length}</span>
        </div>
        <div className="stat-box">
          <span className="stat-label">Total Amount</span>
          <span className="stat-value">
            {formatCurrency(filteredReceipts.reduce((sum, receipt) => sum + Number(receipt.totalAmount), 0))}
          </span>
        </div>
      </div>
      
      {/* Table Section */}
      {filteredReceipts.length === 0 ? (
        <div className="no-records">
          <p>No payment records found matching your criteria.</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="records-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('receiptNo')} className="sortable-header">
                    Receipt No
                    {sortConfig.key === 'receiptNo' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => handleSort('createdAt')} className="sortable-header">
                    Date
                    {sortConfig.key === 'createdAt' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => handleSort('studentName')} className="sortable-header">
                    Student Name
                    {sortConfig.key === 'studentName' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => handleSort('degree')} className="sortable-header">
                    Degree/Class
                    {sortConfig.key === 'degree' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => handleSort('totalAmount')} className="sortable-header">
                    Amount
                    {sortConfig.key === 'totalAmount' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th onClick={() => handleSort('paymentMode')} className="sortable-header">
                    Payment Mode
                    {sortConfig.key === 'paymentMode' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((receipt) => (
                  <tr key={receipt._id}>
                    <td>{receipt.receiptNo}</td>
                    <td>{formatDate(receipt.createdAt)}</td>
                    <td className="student-name-cell">
                      <div className="student-info">
                        <span className="student-name">{receipt.studentName}</span>
                        <span className="user-code">{receipt.userCode}</span>
                      </div>
                    </td>
                    <td>{receipt.degree}</td>
                    <td className="amount-cell">{formatCurrency(receipt.totalAmount)}</td>
                    <td>{receipt.paymentMode}</td>
                    <td className="actions-cell">
                      <button
                        className="view-btn"
                        onClick={() => viewReceipt(receipt.pdfUrl)}
                      >
                        View Receipt
                      </button>
                      <button
                        className="details-btn"
                        onClick={() => viewDetails(receipt)}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="pagination">
            <button 
              onClick={() => goToPage(1)} 
              disabled={currentPage === 1}
              className="page-btn"
            >
              &laquo;
            </button>
            <button 
              onClick={() => goToPage(currentPage - 1)} 
              disabled={currentPage === 1}
              className="page-btn"
            >
              &lsaquo;
            </button>
            
            <div className="page-info">
              Page {currentPage} of {totalPages}
            </div>
            
            <button 
              onClick={() => goToPage(currentPage + 1)} 
              disabled={currentPage === totalPages}
              className="page-btn"
            >
              &rsaquo;
            </button>
            <button 
              onClick={() => goToPage(totalPages)} 
              disabled={currentPage === totalPages}
              className="page-btn"
            >
              &raquo;
            </button>
          </div>
        </>
      )}
      
      {/* Receipt Details Modal */}
      {showDetailsModal && selectedReceipt && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Receipt Details</h2>
              <button 
                className="close-btn"
                onClick={() => setShowDetailsModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="receipt-details">
                <div className="detail-row">
                  <span className="detail-label">Receipt No:</span>
                  <span className="detail-value">{selectedReceipt.receiptNo}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date:</span>
                  <span className="detail-value">{formatDate(selectedReceipt.createdAt)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Student Name:</span>
                  <span className="detail-value">{selectedReceipt.studentName}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">User Code:</span>
                  <span className="detail-value">{selectedReceipt.userCode}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Degree/Class:</span>
                  <span className="detail-value">{selectedReceipt.degree}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Financial Year:</span>
                  <span className="detail-value">{selectedReceipt.financialYear}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Total Amount:</span>
                  <span className="detail-value">{formatCurrency(selectedReceipt.totalAmount)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Payment Mode:</span>
                  <span className="detail-value">{selectedReceipt.paymentMode}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Bank:</span>
                  <span className="detail-value">{selectedReceipt.bank || "N/A"}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Remarks:</span>
                  <span className="detail-value">{selectedReceipt.remarks || "None"}</span>
                </div>
              </div>
              
              {/* Fee breakdown if available */}
              {selectedReceipt.receiptDetails && (
                <div className="fee-breakdown">
                  <h3>Fee Breakdown</h3>
                  <table className="fee-table">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Particular</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {JSON.parse(selectedReceipt.receiptDetails).fees?.map((fee, index) => (
                        <tr key={index}>
                          <td>{fee.sno}</td>
                          <td>{fee.particular}</td>
                          <td>{formatCurrency(fee.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              <div className="modal-actions">
                <button 
                  className="view-btn"
                  onClick={() => viewReceipt(selectedReceipt.pdfUrl)}
                >
                  View Receipt
                </button>
                <button 
                  className="close-modal-btn"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <ToastContainer />
    </div>
  );
}

export default PaymentRecords;